// client/src/components/YieldForecastPanel.tsx

import { useState, useEffect } from "react";
import {
  TrendingUp,
  Loader2,
  AlertTriangle,
  BarChart3,
  Calendar,
  CloudRain,
  Thermometer,
  Droplets,
  Sun,
  Target,
  TrendingDown,
  Info,
  Download,
  RefreshCw,
  MapPin,
  Leaf,
} from "lucide-react";
import type { User } from "../types.ts";
import { useLanguage } from "../contexts/LanguageContext.tsx";

const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";

type ForecastResult = {
  crop: string;
  area: number;
  predictedYield: number;
  confidence: number;
  unit: string;
  factors: {
    weatherDataAvailable: boolean;
    baseYieldPerHectare: string;
  };
  dataSource: string;
};

type ScenarioResult = {
  scenario: string;
  yield: number;
  probability: number;
  description: string;
};

type HistoricalData = {
  year: number;
  yield: number;
  weather: string;
};

interface YieldForecastPanelProps {
  user: User | null;
}

export default function YieldForecastPanel({ user }: YieldForecastPanelProps) {
  const { t } = useLanguage();
  const [area, setArea] = useState<number>(1);
  const crop = "maize"; // Focus on maize only
  const [plantingDate, setPlantingDate] = useState<string>("");
  const [soilType, setSoilType] = useState<string>("loam");
  const [irrigationType, setIrrigationType] = useState<string>("rainfed");
  const [result, setResult] = useState<ForecastResult | null>(null);
  const [scenarios, setScenarios] = useState<ScenarioResult[]>([]);
  const [historicalData, setHistoricalData] = useState<HistoricalData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    "forecast" | "scenarios" | "history"
  >("forecast");

  // Generate mock historical data
  useEffect(() => {
    const generateHistoricalData = () => {
      const currentYear = new Date().getFullYear();
      const data: HistoricalData[] = [];

      for (let i = 4; i >= 0; i--) {
        const year = currentYear - i;
        const baseYield = 1.8; // Focus on maize only
        const variation = (Math.random() - 0.5) * 0.6; // ±30% variation
        const yieldValue = Math.max(0.5, baseYield + variation);
        const weatherConditions = ["Good", "Excellent", "Poor", "Average"][
          Math.floor(Math.random() * 4)
        ];

        data.push({
          year,
          yield: parseFloat(yieldValue.toFixed(2)),
          weather: weatherConditions,
        });
      }

      setHistoricalData(data);
    };

    generateHistoricalData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    setScenarios([]);

    if (!backendUrl) {
      setError(
        "Backend URL not configured. Please check environment variables."
      );
      setLoading(false);
      return;
    }

    try {
      // Add timeout to prevent infinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const res = await fetch(
        `${backendUrl}/api/yield-forecast?area=${area}&crop=maize`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to fetch yield forecast");
      }
      const json: ForecastResult = await res.json();
      setResult(json);

      // Generate scenario analysis
      generateScenarios(json.predictedYield);
    } catch (err: any) {
      console.warn("API failed, using fallback data:", err);

      // Generate fallback data if API fails
      const fallbackResult: ForecastResult = {
        crop: "maize",
        area: area,
        predictedYield: parseFloat((area * 1.8 * 0.404686).toFixed(2)), // Basic calculation
        confidence: 75,
        unit: "tons",
        factors: {
          weatherDataAvailable: false,
          baseYieldPerHectare: "1.8",
        },
        dataSource: "Lesotho Agricultural Averages (Offline Mode)",
      };

      setResult(fallbackResult);
      generateScenarios(fallbackResult.predictedYield);

      // Show warning instead of error
      setError(
        "Using offline estimates. Connect to internet for real-time weather data."
      );
    } finally {
      setLoading(false);
    }
  };

  const generateScenarios = (baseYield: number) => {
    const scenarios: ScenarioResult[] = [
      {
        scenario: "Optimal Conditions",
        yield: baseYield * 1.3,
        probability: 20,
        description: "Perfect weather, optimal irrigation, no pests",
      },
      {
        scenario: "Good Conditions",
        yield: baseYield * 1.1,
        probability: 35,
        description: "Favorable weather, adequate water supply",
      },
      {
        scenario: "Average Conditions",
        yield: baseYield,
        probability: 30,
        description: "Normal weather patterns, standard practices",
      },
      {
        scenario: "Poor Conditions",
        yield: baseYield * 0.7,
        probability: 15,
        description: "Drought, pest issues, or extreme weather",
      },
    ];

    setScenarios(
      scenarios.map((s) => ({
        ...s,
        yield: parseFloat(s.yield.toFixed(2)),
      }))
    );
  };

  const exportData = () => {
    if (!result) return;

    const data = {
      forecast: result,
      scenarios,
      historicalData,
      parameters: { area, crop, plantingDate, soilType, irrigationType },
      generatedAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `yield-forecast-${crop}-${
      new Date().toISOString().split("T")[0]
    }.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-3 rounded-xl shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-white/20 rounded-lg">
              <TrendingUp className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Maize Yield Forecasting</h1>
              <p className="text-green-100 mt-1">
                AI-powered maize yield predictions for Lesotho farmers
              </p>
            </div>
          </div>
          {result && (
            <button
              onClick={exportData}
              className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          )}
        </div>
      </div>

      {/* Input Form */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Target className="w-5 h-5 mr-2 text-green-600" />
          Forecast Parameters
        </h2>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Leaf className="w-4 h-4 inline mr-1" />
              Crop Type
            </label>
            <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-medium">
              🌽 Maize (Corn)
            </div>
            <input type="hidden" value="maize" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="w-4 h-4 inline mr-1" />
              Farm Area (acres)
            </label>
            <input
              type="number"
              value={area || ''}
              min={0.1}
              step={0.1}
              onChange={(e) => setArea(parseFloat(e.target.value) || 1)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              Planting Date
            </label>
            <input
              type="date"
              value={plantingDate}
              onChange={(e) => setPlantingDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Soil Type
            </label>
            <select
              value={soilType}
              onChange={(e) => setSoilType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="clay">Clay</option>
              <option value="loam">Loam</option>
              <option value="sandy">Sandy</option>
              <option value="silt">Silt</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Droplets className="w-4 h-4 inline mr-1" />
              Irrigation
            </label>
            <select
              value={irrigationType}
              onChange={(e) => setIrrigationType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="rainfed">Rainfed</option>
              <option value="drip">Drip Irrigation</option>
              <option value="sprinkler">Sprinkler</option>
              <option value="flood">Flood Irrigation</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-medium rounded-lg hover:from-green-600 hover:to-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Generate Forecast
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-yellow-800 font-medium">Notice</h3>
              <p className="text-yellow-700 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-6">
          {/* Tab Navigation */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="flex border-b border-gray-200">
              {[
                { id: "forecast", label: "Main Forecast", icon: TrendingUp },
                {
                  id: "scenarios",
                  label: "Scenario Analysis",
                  icon: BarChart3,
                },
                { id: "history", label: "Historical Data", icon: Calendar },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as any)}
                  className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors ${
                    activeTab === id
                      ? "bg-green-50 text-green-700 border-b-2 border-green-500"
                      : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            <div className="p-6">
              {activeTab === "forecast" && (
                <div className="space-y-6">
                  {/* Main Result */}
                  <div className="text-center bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-xl border border-green-200">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <TrendingUp className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                      Predicted Maize Yield: {result.predictedYield}{" "}
                      {result.unit}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      For {result.area} acres of maize
                    </p>
                    <div className="flex items-center justify-center space-x-4">
                      <div
                        className={`px-4 py-2 rounded-full text-sm font-medium ${
                          result.confidence >= 80
                            ? "bg-green-100 text-green-800"
                            : result.confidence >= 60
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {result.confidence}% Confidence
                      </div>
                      <div className="text-sm text-gray-500">
                        {result.factors.baseYieldPerHectare} tons/hectare base
                      </div>
                    </div>
                  </div>

                  {/* Weather Factors */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <CloudRain className="w-5 h-5 text-green-600" />
                        <h4 className="font-medium text-green-800">
                          Weather Data
                        </h4>
                      </div>
                      <p
                        className={`text-sm ${
                          result.factors.weatherDataAvailable
                            ? "text-green-600"
                            : "text-orange-600"
                        }`}
                      >
                        {result.factors.weatherDataAvailable
                          ? "✓ NASA POWER API"
                          : "⚠ Using defaults"}
                      </p>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <Thermometer className="w-5 h-5 text-green-600" />
                        <h4 className="font-medium text-green-800">
                          Soil Type
                        </h4>
                      </div>
                      <p className="text-sm text-green-600 capitalize">
                        {soilType}
                      </p>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <Droplets className="w-5 h-5 text-green-600" />
                        <h4 className="font-medium text-green-800">
                          Irrigation
                        </h4>
                      </div>
                      <p className="text-sm text-green-600 capitalize">
                        {irrigationType}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "scenarios" && scenarios.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold mb-4">
                    Yield Scenarios
                  </h3>
                  {scenarios.map((scenario, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 p-4 rounded-lg border"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-800">
                          {scenario.scenario}
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-green-600">
                            {scenario.yield} tons
                          </span>
                          <span className="text-sm text-gray-500">
                            ({scenario.probability}% probability)
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">
                        {scenario.description}
                      </p>
                      <div className="mt-2 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${scenario.probability}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "history" && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold mb-4">
                    Historical Yield Data
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {historicalData.map((data, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 p-4 rounded-lg border"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-800">
                            {data.year}
                          </h4>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              data.weather === "Excellent"
                                ? "bg-green-100 text-green-800"
                                : data.weather === "Good"
                                ? "bg-blue-100 text-blue-800"
                                : data.weather === "Average"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {data.weather}
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-800">
                          {data.yield} tons
                        </p>
                        <p className="text-sm text-gray-600">per hectare</p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-start space-x-2">
                      <Info className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-green-800 mb-1">
                          Historical Analysis
                        </h4>
                        <p className="text-sm text-green-700">
                          Average yield over 5 years:{" "}
                          {(
                            historicalData.reduce(
                              (sum, d) => sum + d.yield,
                              0
                            ) / historicalData.length
                          ).toFixed(2)}{" "}
                          tons/hectare
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
